import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { API_URL } from '../config/apiConfig';
import { getPasswordEndpoints } from '../utils/passwordEndpoints';
import React, { useEffect, useState } from 'react';

const ForgotPassword = () => {
  const { role = 'user' } = useParams();
  const [email, setEmail] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      let derivedEmail = '';
      if (role === 'driver') {
        const driver = JSON.parse(localStorage.getItem('driver') || 'null');
        derivedEmail = driver?.email || driver?.data?.email || '';
      } else if (role === 'vendor') {
        const vendor = JSON.parse(localStorage.getItem('vendorData') || 'null');
        derivedEmail = vendor?.email || vendor?.data?.email || '';
      } else if (role === 'admin') {
        const admin = JSON.parse(localStorage.getItem('user') || 'null');
        derivedEmail = admin?.user?.email || admin?.data?.email || admin?.email || '';
      } else {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        derivedEmail = user?.user?.email || user?.data?.email || user?.email || '';
      }
      if (derivedEmail) setEmail(derivedEmail);
    } catch (_) {}
  }, [role]);

  // Centralized endpoints for current role
  const endpoints = getPasswordEndpoints(role);
  const forgotUrl = endpoints.changePasswordUrl;
  const loginUrl = endpoints.loginUrl;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !oldPassword || !newPassword) {
      toast.error('Please fill in all fields.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(forgotUrl, { email, oldPassword, newPassword });
      if (response.data?.success) {
        toast.success(response.data.message || 'Password updated successfully');
        navigate(loginUrl);
      } else {
        toast.error(response.data?.message || 'Failed to update password');
      }
    } catch (err) {
      console.error('Forgot password error:', err.response?.data || err.message);
      toast.error(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
        <div className="text-center">
          <h2 className="mt-2 text-3xl font-bold text-gray-800">Change Password</h2>
          <p className="mt-2 text-sm text-gray-500">
            Enter your email, current password, and new password below.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="on">
          <div className="space-y-5">

            {/* Email Field */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaEnvelope className="h-5 w-5 text-indigo-500" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                readOnly={Boolean(email)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Your email address"
              />
            </div>

            {/* Old Password Field */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="h-5 w-5 text-indigo-500" />
              </div>
              <input
                id="oldPassword"
                name="oldPassword"
                type={showOldPassword ? 'text' : 'password'}
                required
                autoComplete="off"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Current Password"
              />
              <button
                type="button"
                onClick={() => setShowOldPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-500 hover:text-indigo-700 focus:outline-none"
              >
                {showOldPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* New Password Field */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="h-5 w-5 text-indigo-500" />
              </div>
              <input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="New Password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-500 hover:text-indigo-700 focus:outline-none"
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150"
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>

        <div className="text-sm text-center">
          <Link to={loginUrl} className="font-medium text-indigo-600 hover:text-indigo-500 transition">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
